import { useTranslation } from "react-i18next";
import type { Api } from "../../services/api";

type VisualAbstractBlocker = Extract<
  Api["get"]["/webapp/private/visual-abstracts/:id"]["output"]["body"],
  { status: "ready" }
>["blockers"][number];

/**
 * Why a ready poster cannot be shared yet, in the reader's words.
 *
 * The API deliberately ships only the blocker CODE. What it used to ship was
 * the evidence audit's own sentence — "Claim 2: inferential reframing… not
 * directly stated" — which is us talking to ourselves about our QA, printed to
 * an HCP who asked for a poster. The claim-level detail still exists, in
 * `validation` on the row, where the admin and the medical review read it.
 *
 * One line per DISTINCT code: `figure_unavailable` is pushed once per missing
 * figure, and telling the reader the same thing twice reads as two problems.
 */
export function VisualAbstractBlockerNotice({
  blockers,
}: {
  blockers: VisualAbstractBlocker[];
}): React.ReactElement | null {
  const { t } = useTranslation();
  const codes = [...new Set(blockers.map((blocker) => blocker.code))];
  if (codes.length === 0) return null;
  return (
    <div className="rounded-xl bg-ds-cream p-3">
      <p className="m-0 text-footnote font-semibold text-ds-mint-900">
        {t("A few checks are still running before this can be shared:")}
      </p>
      <ul className="mb-0 mt-2 pl-5 text-footnote text-ds-slate-600">
        {codes.map((code) => (
          <li key={code}>
            <BlockerReason code={code} />
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * One literal translation call per arm, rather than a code → key map:
 * `i18next-scanner` extracts keys by reading source text, so a `t(KEYS[code])`
 * indirection would leave every one of these strings out of the locale files and
 * ship English to every other language.
 */
function BlockerReason({
  code,
}: {
  code: VisualAbstractBlocker["code"];
}): React.ReactElement {
  const { t } = useTranslation();
  switch (code) {
    case "to_confirm":
      return <>{t("Some statements still need to be confirmed.")}</>;
    case "source_mismatch":
      return (
        <>{t("Some statements are still being checked against the article.")}</>
      );
    case "language_mismatch":
      return <>{t("Some of the text is not in the poster's language.")}</>;
    case "figure_unavailable":
      return <>{t("A figure from the article could not be included.")}</>;
    case "figure_rights":
      return (
        <>
          {t(
            "The article's licence does not allow republishing one of its figures."
          )}
        </>
      );
    case "patient_privacy":
      return (
        <>{t("Case-report images need a privacy review before sharing.")}</>
      );
    case "accessibility":
      return (
        <>{t("The poster needs an accessibility check before sharing.")}</>
      );
    default:
      throw new Error(code satisfies never);
  }
}
